import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import MenuPanel from './MenuPanel';
import { useT } from '../i18n/i18n';

/**
 * The actions a row has that are not its main one.
 *
 * <p>A ROW'S BUTTONS CANNOT KEEP GROWING. Every feature switched on added another — progress,
 * give, move — until a program carried seven and filled the width. Widening does not fix a list
 * that grows; putting all but the main ones behind one control does, and stays the same size
 * however many features an account turns on.
 *
 * <p>Built on the same pattern as the account menu: click outside or press Escape to close,
 * and the panel is announced as a menu. Unlike that one there are many of these on a page, so
 * each takes an id of its own.
 */
export default function RowMenu({
  label,
  text,
  primary = false,
  alignStart = false,
  children,
}: {
  label: string;
  text?: string;
  primary?: boolean;
  alignStart?: boolean;
  children: ReactNode;
}) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const panelId = useId();
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (wrapRef.current?.contains(t) || panelRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className={alignStart ? "rowmenu start" : "rowmenu"} ref={wrapRef}>
      <button
        ref={triggerRef}
        type="button"
        className={primary ? "btn rowmenu-trigger" : "btn secondary rowmenu-trigger"}
        /* The row it sits in is draggable, and a press that starts a drag would open nothing. */
        draggable={false}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={panelId}
        aria-label={label}
        onClick={() => setOpen((v) => !v)}
      >
        {text ?? t('More')}<span className="usermenu-caret" aria-hidden="true">▾</span>
      </button>

      {open && (
        /* Closing on any choice: every item in here goes somewhere or does something, so
           leaving the panel open over the result would only be in the way. */
        <MenuPanel
          anchor={triggerRef}
          panelRef={panelRef}
          align={alignStart ? 'start' : 'end'}
          id={panelId}
          className="usermenu-panel rowmenu-panel"
          onClick={() => setOpen(false)}
        >
          {children}
        </MenuPanel>
      )}
    </div>
  );
}
